import { useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigateWithParams } from "../../../hooks/useNavigateWithParams";
import { config as appConfig } from "../../../config";
import { AppRoutes } from "../../../routes/AppRoutes";
import { isAppSource } from "../../../helpers/getSourceFromUrl";
import {
  useGetCurrentSubscriptionsOptionsQuery,
  useEditOptionMutation,
  useEditSubscriptionMutation,
  useExtendSubscriptionMutation
} from "../../../rtk/currentSubscriptionsOptions/currentSubscriptionsOptions";
import { SubscriptionItem, OptionItem } from "../../../types/rtk/currentSubscriptionsOptions/currentSubscriptionsOptions";
import { useGetMeanQuery } from "../../../rtk/mean/mean";

import Text from "../../atoms/Text/Text";
import Header from "../../templates/Header/Header";
import SpinnerLoader from "../../atoms/SpinnerLoader/SpinnerLoader";
import ManageSubscriptionsOptionsList from "../../organisms/ManageSubscriptionsOptionsList/ManageSubscriptionsOptionsList";
import PopIn from "../../molecules/PopIn/PopIn";
import ModalPromotionalCode from "../../molecules/ModalSubscriptionGiftCard/ModalSubscriptionGiftCard";

/** Error details of the extend endpoint the user can act on, with the message shown in the modal. */
const EXTEND_ERRORS: Record<string, string> = {
  giftcard_not_found: "subscription:MANAGE_PAGE.EXTEND.MODAL.ERROR.INVALID",
  giftcard_not_usable: "subscription:MANAGE_PAGE.EXTEND.MODAL.ERROR.INVALID",
  giftcard_not_eligible: "subscription:MANAGE_PAGE.EXTEND.MODAL.ERROR.NOT_ELIGIBLE",
};

const ManageSubscriptions = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithParams();

  const { data: subsOptsData, isLoading: subsOptsLoading, error: subsOptsError } = useGetCurrentSubscriptionsOptionsQuery();
  const [editOption, { isLoading: isLoadingOption }] = useEditOptionMutation();
  const [editSubscription, { isLoading: isLoadingSubscription }] = useEditSubscriptionMutation();
  const [extendSubscription] = useExtendSubscriptionMutation();
  const { data: meanData, isLoading: meanLoading } = useGetMeanQuery();

  const isDisabled = isLoadingOption || isLoadingSubscription;
  const [paymentMeanPopIn, setPaymentMeanPopIn] = useState<{
    open: boolean;
    title?: string;
    subtitle?: string;
  }>({ open: false });
  const [extendModal, setExtendModal] = useState<{ open: boolean; error?: string }>({ open: false });

  const handleActivate = async (item: SubscriptionItem | OptionItem) => {
    if (isDisabled) return;

    if (meanData?.default_payment_mean == null || meanData?.default_payment_mean?.valid === false || meanData?.default_payment_mean?.expired === true) {
      setPaymentMeanPopIn({
        open: true,
        title: item.type === "subscription"
          ? t("subscription:MANAGE_PAGE.INVALID_PAYMENT_MEAN_POPIN.TITLE.SUBSCRIPTION")
          : t("subscription:MANAGE_PAGE.INVALID_PAYMENT_MEAN_POPIN.TITLE.OPTION"),
        subtitle: meanData?.default_payment_mean == null
          ? t("subscription:MANAGE_PAGE.INVALID_PAYMENT_MEAN_POPIN.SUBTITLE.MISSING")
          : meanData.default_payment_mean.expired === true
            ? t("subscription:MANAGE_PAGE.INVALID_PAYMENT_MEAN_POPIN.SUBTITLE.EXPIRED")
            : t("subscription:MANAGE_PAGE.INVALID_PAYMENT_MEAN_POPIN.SUBTITLE.ERRORS"),
      });
      return;
    }

    try {
      if (item.type === "subscription") {
        await editSubscription({
          renewal_active: true
        }).unwrap();
      } else {
        await editOption({
          option_code: item.code,
          renewal_active: true
        }).unwrap();
      }
    } catch (error) {
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'error' } });
    }
  };

  const handleEditButton = (item: SubscriptionItem | OptionItem) => {
    if (isDisabled) return;
    const route = item.type === "option"
      ? AppRoutes.MANAGE_SUBSCRIPTIONS_CANCEL_CONFIRM
      : AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT;

    navigate(route, {
      state: { ...item, tracker_name: subsOptsData?.tracker_name }
    });
  };

  const handleExtend = async (giftcard: string | null): Promise<boolean> => {
    if (!giftcard) return false;
    try {
      const subscription = await extendSubscription({ giftcard }).unwrap();
      setExtendModal({ open: false });
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, {
        state: {
          context: "extend_success",
          tracker_name: subsOptsData?.tracker_name,
          next_charge_at: subscription.next_charge_at,
        }
      });
      return true;
    } catch (error) {
      const errorKey = EXTEND_ERRORS[(error as { data?: { detail?: string } })?.data?.detail ?? ""];
      if (errorKey) {
        setExtendModal({ open: true, error: t(errorKey) });
        return false;
      }
      setExtendModal({ open: false });
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: "error" } });
      return true;
    }
  };

  return (
    <>
      {!isAppSource() && (
        <Header
          headerStep={{ context: "manage" }}
          title={t("subscription:MANAGE_PAGE.TITLE")}
          goBack={() => window.location.href = appConfig.REACT_APP_FE_BASE_URL}
        />
      )}
      <Box className="ManageSubscriptions">
        {(subsOptsLoading) && <SpinnerLoader />}

        {!subsOptsLoading && subsOptsError && (
          <Text children="Error while retrieving subscriptions." />
        )}

        {!subsOptsLoading && !meanLoading && subsOptsData && (
          <ManageSubscriptionsOptionsList
            data={subsOptsData}
            onActivate={handleActivate}
            onEdit={handleEditButton}
            onExtend={() => setExtendModal({ open: true })}
            isDisabled={isDisabled}
          />
        )}
      </Box>
      <ModalPromotionalCode
        isModalOpen={extendModal.open}
        title={t("subscription:MANAGE_PAGE.EXTEND.MODAL.TITLE")}
        description={t("subscription:MANAGE_PAGE.EXTEND.MODAL.DESCRIPTION", { tracker_name: subsOptsData?.tracker_name ?? "" })}
        current={null}
        handleClose={() => setExtendModal({ open: false })}
        handleValidate={handleExtend}
        mode="giftCard"
        errorMessage={extendModal.error}
      />
      <PopIn
        isOpen={paymentMeanPopIn.open}
        onClose={() => setPaymentMeanPopIn({ open: false })}
        title={paymentMeanPopIn.title ?? ""}
        description={paymentMeanPopIn.subtitle}
        buttonLabel={t("subscription:MANAGE_PAGE.INVALID_PAYMENT_MEAN_POPIN.BUTTON")}
        onButtonClick={() => {
          const params = new URLSearchParams({ origin: "manage-subscriptions" });
          navigate(`${AppRoutes.MANAGE_PAYMENTS}?${params}`);
          console.log("Navigating to manage payments due to invalid payment mean");
        }}
      />
    </>
  );
};

export default ManageSubscriptions;
